import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import type { BrandImage, ProductTranslationParams } from '@go-modules/interfaces/brands/brand.interface';
import type { ProductName } from '@go-modules/interfaces/brands/product-name.type';
import { NgxFeatureFlagService } from '../feature-flag/feature-flag.service';

interface BrandMetadata {
	favicon: BrandImage;
	fullLogo: BrandImage;
	spotLogo: BrandImage;
	login: BrandImage;
	signup: BrandImage;
	shapes: BrandImage;
}

@Injectable({
	providedIn: 'root'
})
export class ActiveBrandService {
	private static readonly BRAND_METADATA: Record<ProductName, BrandMetadata> = {
		GoReact: {
			favicon: {
				src: 'https://staticassets.goreact.com/favicon-2022.svg',
				alt: ''
			},
			fullLogo: {
				src: 'https://staticassets.goreact.com/logo-goreact-dash-2022.svg',
				alt: 'GoReact'
			},
			spotLogo: {
				src: 'https://staticassets.goreact.com/logo-goreact-spot-2022.svg',
				alt: 'GoReact'
			},
			login: {
				src: 'https://staticassets.goreact.com/login.png',
				alt: ''
			},
			signup: {
				src: 'https://staticassets.goreact.com/sign-up.png',
				alt: ''
			},
			shapes: {
				src: 'https://staticassets.goreact.com/shapes.svg',
				alt: ''
			}
		},
		EchoReflect: {
			favicon: {
				src: 'https://staticassets.goreact.com/favicon-2022-echo.svg',
				alt: ''
			},
			fullLogo: {
				src: 'https://staticassets.goreact.com/logo-echoreflect-dash-2022.svg',
				alt: 'EchoReflect'
			},
			spotLogo: {
				src: 'https://staticassets.goreact.com/logo-echoreflect-spot-2022.svg',
				alt: 'EchoReflect'
			},
			login: {
				src: 'https://staticassets.goreact.com/login-echoreflect.png',
				alt: ''
			},
			signup: {
				src: 'https://staticassets.goreact.com/sign-up-echoreflect.png',
				alt: ''
			},
			shapes: {
				src: 'https://staticassets.goreact.com/shapes-echo.svg',
				alt: ''
			}
		}
	};

	constructor (
		private featureFlagService: NgxFeatureFlagService,
		@Inject(DOCUMENT) private document: Document
	) { }

	public isEchoReflect (): boolean {
		return this.featureFlagService.isAvailable('ECHOREFLECT_REBRAND');
	}

	public getProductName (): ProductName {
		return this.isEchoReflect() ? 'EchoReflect' : 'GoReact';
	}

	public getTranslationParams (): ProductTranslationParams {
		return { product: this.getProductName() };
	}

	public getFavicon (): BrandImage {
		return { ...ActiveBrandService.BRAND_METADATA[this.getProductName()].favicon };
	}

	public applyDocumentBranding (titleSuffix: string): void {
		this.document.title = `${this.getProductName()} - ${titleSuffix}`;
		const favicon = this.document.querySelector<HTMLLinkElement>('#app-favicon');
		if (favicon != null) {
			favicon.href = this.getFavicon().src;
		}
	}

	public getFullLogo (productName: ProductName = this.getProductName()): BrandImage {
		return { ...ActiveBrandService.BRAND_METADATA[productName].fullLogo };
	}

	public getSpotLogo (productName: ProductName = this.getProductName()): BrandImage {
		return { ...ActiveBrandService.BRAND_METADATA[productName].spotLogo };
	}

	public getLoginImage (): BrandImage {
		return { ...ActiveBrandService.BRAND_METADATA[this.getProductName()].login };
	}

	public getSignUpImage (): BrandImage {
		return { ...ActiveBrandService.BRAND_METADATA[this.getProductName()].signup };
	}

	public getImage (imageName: string): string {
		const fullName = this.isEchoReflect()
			? imageName.replace(/^(.*)(\.svg|\.png)$/, '$1-echo$2')
			: imageName;
		return `https://staticassets.goreact.com/${fullName}` ;
	}

	public getShapesImage (): BrandImage {
		return { ...ActiveBrandService.BRAND_METADATA[this.getProductName()].shapes };
	}
}
